import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ArrowUpRight, MapPin, Globe, Search, FileText, Share2, Inbox, Zap } from "lucide-react";
import Seo from "@/components/Seo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const SOLUTIONS = [
  {
    icon: Globe,
    title: "Professional Website",
    desc: "A fast, mobile-friendly website designed to clearly present the business, its services and coverage areas, with straightforward ways for customers to make contact or request a quote.",
  },
  {
    icon: Search,
    title: "Local SEO",
    desc: "On-page and technical optimisation so search engines understand the services offered and the areas covered across Greater London.",
  },
  {
    icon: FileText,
    title: "Quote Capture",
    desc: "A structured enquiry flow that captures the vehicle, the fault and the location — so every quote request arrives with the detail needed to respond.",
  },
  {
    icon: MapPin,
    title: "Google Presence",
    desc: "Google Business Profile setup to help the business appear when nearby drivers search for auto electrical help.",
  },
  {
    icon: Share2,
    title: "Facebook Business Setup",
    desc: "A professional Facebook business presence with consistent branding, giving customers another trusted way to find and message the business.",
  },
  {
    icon: Inbox,
    title: "Customer Enquiry System",
    desc: "Website enquiries are captured through a structured form and directed to the business, giving customers a simple way to request help and providing the business with the information needed to follow up.",
  },
];

export default function CaseStudy() {
  return (
    <div data-testid="case-study-page">
      <Seo
        title="Case Study: One Time Auto Electrical Repairs — SourceWorld Automations"
        description="How SourceWorld Automations built a complete digital presence for One Time Auto Electrical Repairs, a mobile auto electrician serving Greater London."
      />
      <Navbar />
      <main className="pt-[72px]">
        <section className="relative grid-bg overflow-hidden">
          <div className="pointer-events-none absolute -top-32 left-[-10%] w-[520px] h-[520px] rounded-full bg-sky-500/10 blur-[140px]" />
          <div className="section-shell relative pt-14 pb-16 sm:pt-20 sm:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="micro-label">Case Study</p>
                <h1 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-[1.08]">
                  One Time Auto Electrical Repairs
                </h1>
                <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-sky-400 flex items-center gap-2">
                  <MapPin size={13} /> Mobile auto electrician · Greater London
                </p>
                <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-400 max-w-xl">
                  A mobile auto electrical specialist covering Greater London — bringing diagnostic and
                  repair services directly to the customer. Our brief: build a complete online presence
                  that helps customers across Greater London find the business and make an enquiry.
                </p>
              </Reveal>
            </div>
            <Reveal className="lg:col-span-5" delay={0.1}>
              <div className="rounded-3xl overflow-hidden border border-sky-500/20 vignette" style={{ clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 44px), calc(100% - 44px) 100%, 0 100%)" }}>
                <img
                  src="https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxtZWNoYW5pYyUyMHdvcmtzaG9wJTIwY2FyJTIwcmVwYWlyfGVufDB8fHx8MTc5MDc5NzU3Mnww&ixlib=rb-4.1.0&q=85"
                  alt="Auto electrical diagnostic and repair work in progress"
                  className="w-full h-64 sm:h-80 object-cover"
                />
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 sm:py-24">
          <div className="section-shell grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="micro-label">The Challenge</p>
                <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                  A Stronger, More Connected Digital Presence
                </h2>
                <p className="mt-5 text-sm sm:text-base leading-relaxed text-slate-400">
                  One Time Auto Electrical Repairs needed a stronger, more connected digital presence
                  that made its mobile auto electrical services clear and gave customers across Greater
                  London a straightforward way to discover the business and make an enquiry.
                </p>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-400">
                  The goal was to bring the website, local search visibility, enquiry capture and
                  business social presence together into one professional customer journey.
                </p>
              </Reveal>
            </div>
            <Reveal className="lg:col-span-7" delay={0.1}>
              <div className="rounded-3xl overflow-hidden border border-sky-500/20 vignette h-full">
                <img
                  src="https://images.unsplash.com/photo-1643701079732-3b1c7a797e3d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxtZWNoYW5pYyUyMHdvcmtzaG9wJTIwY2FyJTIwcmVwYWlyfGVufDB8fHx8MTc5MDc5NzU3Mnww&ixlib=rb-4.1.0&q=85"
                  alt="Technician checking vehicle electrical systems underneath a car"
                  className="w-full h-72 lg:h-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-navy-900/40 border-y border-sky-500/10">
          <div className="section-shell">
            <Reveal>
              <p className="micro-label">The Solution</p>
              <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white max-w-2xl">
                Six connected pieces, one complete system
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {SOLUTIONS.map((s, i) => (
                <Reveal key={s.title} delay={0.05 * (i % 3)}>
                  <article data-testid={`case-solution-${i + 1}`} className="card-hover h-full rounded-2xl border border-sky-500/12 bg-navy-950/70 p-7">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400">
                      <s.icon size={20} strokeWidth={1.8} />
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-bold text-white tracking-tight">{s.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                  </article>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <div className="mt-10 glass-panel rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <Zap size={26} className="text-sky-400 shrink-0" strokeWidth={1.8} />
                <p className="text-sm sm:text-base leading-relaxed text-slate-300">
                  This case study is a living project. As the project matures, verified outcomes can
                  be added here with the client's permission, providing an honest account of what was
                  built and how it performs.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <a
                href="https://onetimeautoelectrical.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="case-study-visit-live-below-note"
                className="btn-primary mt-8"
              >
                Visit Live Website <ArrowUpRight size={16} strokeWidth={2.5} />
              </a>
            </Reveal>
          </div>
        </section>

        <section className="py-16 sm:py-24">
          <div className="section-shell text-left grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <Reveal className="lg:col-span-8">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
                Want a system like this for your business?
              </h2>
              <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl leading-relaxed">
                Tell us what you do and where you work — we'll show you exactly what your version of this
                setup would look like.
              </p>
            </Reveal>
            <Reveal className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4 lg:items-end" delay={0.12}>
              <Link to="/#contact" data-testid="case-study-cta-contact" className="btn-primary">
                Get a Free Consultation <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
              <a
                href="https://onetimeautoelectrical.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="case-study-visit-live"
                className="btn-ghost"
              >
                Visit Live Website <ArrowUpRight size={16} strokeWidth={2.5} />
              </a>
              <Link to="/" data-testid="case-study-back-home" className="btn-ghost">
                <ArrowLeft size={15} strokeWidth={2.5} /> Back to Homepage
              </Link>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
